import { Box, Dialog, DialogActions, Grid, TableCell } from '@mui/material';
import { FC } from 'react';
import { ReactComponent as WarningIcon } from '../../assets/images/serviceCodeWarningIcon.svg';
import MediumTypography from '../../components/formlib/MediumTypography';
import { ProgressNoteServiceCodeVisitType } from '../../services/progressNotes/ProgressNotesApi';
import ButtonComponent from '../../components/formlib/ButtonComponent';

export interface ServiceCodeValidationDataProps {
  showModal: boolean;
  visits: ProgressNoteServiceCodeVisitType[];
}
interface ServiceCodeValidationModalProps {
  data: ServiceCodeValidationDataProps;
  onModalClose: () => void;
  onProceed: () => void;
}

const ServiceCodeValidationModal: FC<ServiceCodeValidationModalProps> = ({
  data,
  onModalClose,
  onProceed,
}) => {
  return (
    <Dialog
      open={data.showModal}
      onClose={(_, reason) => {
        if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
          onModalClose();
        }
      }}
      fullWidth
      disableEscapeKeyDown
      maxWidth={'lg'}
    >
      <Box className="flex__" sx={{ padding: '24px 20px 0px 20px' }}>
        <Box className="mr-lg">
          <WarningIcon />
        </Box>
        <Grid container>
          <Grid item md={12} className="mb-md">
            <MediumTypography
              className="mainText-xlg"
              labelid="serviceCodeValidationTitleText"
              defaultlabel="Service Code(s) Validations"
            />
            <MediumTypography
              labelid="serviceCodeValidationSubtitleText"
              defaultlabel="Progress notes cannot be completed due to validation errors associated with the selected service code(s)."
            />
          </Grid>

          {data.visits.map((visit, index) => {
            return (
              <Box
                sx={{ width: '100%' }}
                className="mb-md"
                key={`visit-${index + 1}`}
              >
                <Grid item md={12}>
                  <MediumTypography
                    className="font-bold mb-xs"
                    label={visit.serviceCodeLabel ?? '-'}
                  />
                </Grid>
                <Box
                  sx={{ border: '1px solid var(--thom-mono-gray2)' }}
                  className="borderRadius4"
                >
                  <Grid item md={12}>
                    <TableCell className="disableBorderBotton">
                      <MediumTypography
                        textColor={`${
                          visit.isMaxHoursPerDayBreached
                            ? 'var(--thom-radical-red)'
                            : 'var(--thom-mono-gray)'
                        }`}
                        labelid="maxHoursPerDayText"
                        defaultlabel="Max Hours Per Day"
                      />
                      <MediumTypography
                        className="font-bold"
                        label={visit.maxHoursPerDay?.toString() ?? '-'}
                        textColor={`${
                          visit.isMaxHoursPerDayBreached
                            ? 'var(--thom-radical-red)'
                            : 'var(thom-dark-green-table-black)'
                        }`}
                      />
                    </TableCell>

                    <TableCell className="disableBorderBotton">
                      <MediumTypography
                        textColor={`${
                          visit.isMaxHoursPerVisitBreached
                            ? 'var(--thom-radical-red)'
                            : 'var(--thom-mono-gray)'
                        }`}
                        labelid="maxHoursPerVisitText"
                        defaultlabel="Max Hours Per Visit"
                      />
                      <MediumTypography
                        className="font-bold"
                        textColor={`${
                          visit.isMaxHoursPerVisitBreached
                            ? 'var(--thom-radical-red)'
                            : 'var(thom-dark-green-table-black)'
                        }`}
                        label={visit.maxHoursPerVisit?.toString() ?? '-'}
                      />
                    </TableCell>

                    <TableCell className="disableBorderBotton">
                      <MediumTypography
                        textColor={`${
                          visit.isMaxHoursPerWeekBreached
                            ? 'var(--thom-radical-red)'
                            : 'var(--thom-mono-gray)'
                        }`}
                        labelid="maxHoursPerWeekText"
                        defaultlabel="Max Hours Per Week"
                      />
                      <MediumTypography
                        className="font-bold"
                        textColor={`${
                          visit.isMaxHoursPerWeekBreached
                            ? 'var(--thom-radical-red)'
                            : 'var(thom-dark-green-table-black)'
                        }`}
                        label={visit.maxHoursPerWeek?.toString() ?? '-'}
                      />
                    </TableCell>

                    <TableCell className="disableBorderBotton">
                      <MediumTypography
                        textColor={`${
                          visit.isMaxVisitsPerDisciplineBreached
                            ? 'var(--thom-radical-red)'
                            : 'var(--thom-mono-gray)'
                        }`}
                        labelid="maxHoursPerSisciplineText"
                        defaultlabel="Max Hours Per Discipline"
                      />
                      <MediumTypography
                        className="font-bold"
                        textColor={`${
                          visit.isMaxVisitsPerDisciplineBreached
                            ? 'var(--thom-radical-red)'
                            : 'var(thom-dark-green-table-black)'
                        }`}
                        label={visit.maxVisitsPerDiscipline?.toString() ?? '-'}
                      />
                    </TableCell>

                    <TableCell className="disableBorderBotton">
                      <MediumTypography
                        textColor={`${
                          visit.isMaxVisitsPerDayBreached
                            ? 'var(--thom-radical-red)'
                            : 'var(--thom-mono-gray)'
                        }`}
                        labelid="maxVisitsPerDayText"
                        defaultlabel="Max Visit Per Day"
                      />
                      <MediumTypography
                        className="font-bold"
                        textColor={`${
                          visit.isMaxVisitsPerDayBreached
                            ? 'var(--thom-radical-red)'
                            : 'var(thom-dark-green-table-black)'
                        }`}
                        label={visit.maxVisitsPerDay?.toString() ?? '-'}
                      />
                    </TableCell>

                    <TableCell className="disableBorderBotton">
                      <MediumTypography
                        textColor={`${
                          visit.isMaxVisitsPerWeekBreached
                            ? 'var(--thom-radical-red)'
                            : 'var(--thom-mono-gray)'
                        }`}
                        labelid="maxVisitPerWeekText"
                        defaultlabel="Max Visit Per Week"
                      />
                      <MediumTypography
                        className="font-bold"
                        textColor={`${
                          visit.isMaxVisitsPerWeekBreached
                            ? 'var(--thom-radical-red)'
                            : 'var(thom-dark-green-table-black)'
                        }`}
                        label={visit.maxVisitsPerWeek?.toString() ?? '-'}
                      />
                    </TableCell>
                  </Grid>

                  <Grid item md={12}>
                    <TableCell className="disableBorderBotton">
                      <MediumTypography
                        textColor={`${
                          visit.isMaxVisitsSixMonthsBreached
                            ? 'var(--thom-radical-red)'
                            : 'var(--thom-mono-gray)'
                        }`}
                        labelid="maxVisits6MonthsText"
                        defaultlabel="Max Visits 6 Months"
                      />
                      <MediumTypography
                        className="font-bold"
                        textColor={`${
                          visit.isMaxVisitsSixMonthsBreached
                            ? 'var(--thom-radical-red)'
                            : 'var(thom-dark-green-table-black)'
                        }`}
                        label={visit.maxVisitsSixMonths?.toString() ?? '-'}
                      />
                    </TableCell>

                    <TableCell className="disableBorderBotton">
                      <MediumTypography
                        textColor={`${
                          visit.isMaxVisitsLifetimeBreached
                            ? 'var(--thom-radical-red)'
                            : 'var(--thom-mono-gray)'
                        }`}
                        labelid="maxVisitsLifeTimeText"
                        defaultlabel="Max Visits Lifetime"
                      />
                      <MediumTypography
                        className="font-bold"
                        textColor={`${
                          visit.isMaxVisitsLifetimeBreached
                            ? 'var(--thom-radical-red)'
                            : 'var(thom-dark-green-table-black)'
                        }`}
                        label={visit.maxVisitsLifetime?.toString() ?? '-'}
                      />
                    </TableCell>

                    <TableCell className="disableBorderBotton">
                      <MediumTypography
                        textColor={`${
                          visit.isMaxVisitsPreBirthBreached
                            ? 'var(--thom-radical-red)'
                            : 'var(--thom-mono-gray)'
                        }`}
                        labelid="maxVisitsPrebirthText"
                        defaultlabel="Max Visits Prebirth"
                      />
                      <MediumTypography
                        className="font-bold"
                        textColor={`${
                          visit.isMaxVisitsPreBirthBreached
                            ? 'var(--thom-radical-red)'
                            : 'var(thom-dark-green-table-black)'
                        }`}
                        label={visit.maxVisitsPreBirth?.toString() ?? '-'}
                      />
                    </TableCell>

                    <TableCell className="disableBorderBotton">
                      <MediumTypography
                        textColor={`${
                          visit.isMaxVisitsUnderTwoMonthsBreached
                            ? 'var(--thom-radical-red)'
                            : 'var(--thom-mono-gray)'
                        }`}
                        labelid="maxVisitsUnder2MonthsText"
                        defaultlabel="Max Visits Under 2 Months"
                      />
                      <MediumTypography
                        className="font-bold"
                        textColor={`${
                          visit.isMaxVisitsUnderTwoMonthsBreached
                            ? 'var(--thom-radical-red)'
                            : 'var(thom-dark-green-table-black)'
                        }`}
                        label={visit.maxVisitsUnderTwoMonths?.toString() ?? '-'}
                      />
                    </TableCell>

                    <TableCell className="disableBorderBotton">
                      <MediumTypography
                        textColor={`${
                          visit.isMaxVisitsOverTwoMonthsBreached
                            ? 'var(--thom-radical-red)'
                            : 'var(--thom-mono-gray)'
                        }`}
                        labelid="maxVisitsOver2MonthsText"
                        defaultlabel="Max Visits Over 2 Months"
                      />
                      <MediumTypography
                        className="font-bold"
                        textColor={`${
                          visit.isMaxVisitsOverTwoMonthsBreached
                            ? 'var(--thom-radical-red)'
                            : 'var(thom-dark-green-table-black)'
                        }`}
                        label={visit.maxVisitsOverTwoMonths?.toString() ?? '-'}
                      />
                    </TableCell>
                  </Grid>
                </Box>
              </Box>
            );
          })}
        </Grid>
      </Box>
      <DialogActions>
        <Box className="mb-sm">
          <ButtonComponent
            variantType="contained"
            type="submit"
            className="btn-primary btn-cancel btn_width_90px mr-md"
            labelId={'reviewTravelExpense.close'}
            defaultLabelId={'Close'}
            onClick={onModalClose}
          />
          <ButtonComponent
            variantType="contained"
            type="submit"
            className="btn-primary btn-submit alrt_Width_btn mr-sm"
            labelId={'proceedText'}
            defaultLabelId={'Proceed'}
            onClick={onProceed}
          />
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default ServiceCodeValidationModal;
